import React, { useEffect, useState } from 'react'
import { Col } from 'react-bootstrap'
import Card from 'react-bootstrap/Card'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { convertDate } from '../../../assets/ts/date-and-time/date-and-time'
import { defaultToEmptyString, twoDigits } from '../../../assets/ts/formatting/formatting'
import './charts.css'
import './piecharts.css'

// Function that renders a custom XAxis for the byCategories Chart
const CustomYAxisTick = ({ payload, x, y }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text dx={-10} fill='#666' fontSize='11' textAnchor='start' x={0} y={0}>
        <tspan>
          {payload.value === 0
            ? defaultToEmptyString(payload.value)
            : defaultToEmptyString(twoDigits(payload.value))}
        </tspan>
      </text>
    </g>
  )
}
CustomYAxisTick.displayName = 'CustomYAxis'

const CustomXAxisTick = ({ payload, x, y }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dx={0}
        dy={16}
        fill='#666'
        fontSize='9'
        textAnchor='end'
        transform='rotate(-40)'
        x={0}
        y={0}
      >
        <tspan>
          {payload.value === 0
            ? defaultToEmptyString(payload.value)
            : defaultToEmptyString(twoDigits(payload.value))}
        </tspan>
      </text>
    </g>
  )
}
CustomXAxisTick.displayName = 'CustomXAxis'

const CustomTooltip = ({ active, payload }: any) => {
  if (!payload) return null
  if (active) {
    return (
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '5px',
          boxShadow: '0px 0px 5px',
          padding: '0 3px 0 3px',
        }}
      >
        <h4
          style={{
            display: 'flex',
            fontSize: '14px',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ marginRight: 5 }}>Submitted:</span>{' '}
          <span style={{ alignSelf: 'end', color: '#7E04B6' }}>
            {defaultToEmptyString(twoDigits(payload[0].payload.submitted))}
          </span>
        </h4>
        <h4
          style={{
            display: 'flex',
            fontSize: '14px',
            justifyContent: 'space-between',
            marginTop: 1,
          }}
        >
          <span style={{ marginRight: 5 }}>Resolved:</span>{' '}
          <span style={{ color: '#477A67' }}>
            {defaultToEmptyString(twoDigits(payload[1].payload.resolved))}
          </span>
        </h4>
        <h4
          style={{
            display: 'flex',
            fontSize: '14px',
            justifyContent: 'space-between',
            marginTop: 1,
          }}
        >
          <span style={{ marginRight: 5 }}>Reopened:</span>{' '}
          <span style={{ color: '#5896B8' }}>
            {defaultToEmptyString(twoDigits(payload[2].payload.reopened))}
          </span>
        </h4>
      </div>
    )
  }
  return null
}
CustomTooltip.displayName = 'CustomTooltip'

const TotalsByMonthChart = ({ inquiryFilter, totalByMonthCounts }: any) => {
  const formattedData: any[] = []

  totalByMonthCounts.forEach((monthCounts) => {
    const convertedMonth = convertDate(
      new Date(0, Number(monthCounts.month)),
      'Month'
    ).toUpperCase()
    formattedData.push({
      name: convertedMonth,
      reopened: monthCounts.reopenedCount || 0,
      resolved: monthCounts.resolvedCount || 0,
      submitted: monthCounts.submittedCount || 0,
    })
  })

  return (
    <Col className='align-items-stretch my-2 w-100 d-flex' lg={12} md={12} sm={12}>
      <Card className='w-100'>
        <Card.Body className='d-flex flex-column w-100'>
          <div style={{ alignItems: 'center', display: 'flex', marginTop: 16, width: '100%' }}>
            <p
              style={{
                color: '#355062',
                fontSize: 12,
                marginBottom: 100,
                marginRight: 0,
                padding: 0,
                transform: 'rotate(-90deg)',
              }}
            >
              Inquiries
            </p>
            {formattedData.length > 0 && (
              <ResponsiveContainer height={300} width='98%'>
                <BarChart
                  aria-label='Total By Month Chart'
                  barGap={2}
                  className='tooltiphidefocus'
                  data={formattedData}
                  height={300}
                  margin={{
                    bottom: 5,
                    left: 20,
                    right: 30,
                    top: 5,
                  }}
                  width={500}
                >
                  <CartesianGrid strokeOpacity={0.5} vertical={false} />
                  <XAxis
                    dataKey='name'
                    height={40}
                    interval={0}
                    stroke='#eaf0f4'
                    strokeWidth={2}
                    textAnchor='end'
                    tick={CustomXAxisTick}
                    tickLine={false}
                    type='category'
                  />
                  <YAxis
                    allowDecimals={false}
                    domain={[0, 'dataMax + 5']}
                    interval='preserveStartEnd'
                    stroke='#eaf0f4'
                    strokeWidth={3}
                    tick={CustomYAxisTick}
                    tickLine={false}
                    width={20}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'transparent' }}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey='submitted'
                    fill='#7E04B6'
                    maxBarSize={30}
                    radius={[10, 10, 0, 0]}
                    tabIndex={0}
                  >
                    {formattedData.map((entry) => (
                      <Cell aria-label={entry.name} key={defaultToEmptyString(entry.name)} />
                    ))}
                  </Bar>
                  <Bar
                    dataKey='resolved'
                    fill='#477A67'
                    maxBarSize={30}
                    radius={[10, 10, 0, 0]}
                    tabIndex={0}
                  >
                    {formattedData.map((entry) => (
                      <Cell aria-label={entry.name} key={defaultToEmptyString(entry.name)} />
                    ))}
                  </Bar>
                  <Bar
                    dataKey='reopened'
                    fill='#5896B8'
                    maxBarSize={30}
                    radius={[10, 10, 0, 0]}
                    tabIndex={0}
                  >
                    {formattedData.map((entry) => (
                      <Cell aria-label={entry.name} key={defaultToEmptyString(entry.name)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card.Body>
      </Card>
    </Col>
  )
}

export default TotalsByMonthChart
